<template>
    <div v-if="pendingActivation" class="payout-card-activation g-p-5">
        <g-typography tag="h4" font-weight="semibold">GRIN Pay Debit Card</g-typography>

        <g-typography tag="p" color="grey-6" class="g-mt-3 g-mb-5">
            <g-alert v-if="deliveryFailed" type="error" :message="description" show-icon>
                <template #icon><g-icon name="exclamation-circle" size="small" /></template>
            </g-alert>

            <div v-else>
                {{ description }}
            </div>
        </g-typography>

        <g-typography tag="p" color="grey-8" class="g-mb-3">
            <g-icon name="shipping-fast" size="small" />
            {{ deliveryStatus }}
        </g-typography>

        <g-steps :current="currentStepId" direction="vertical" :progress-dot="true">
            <g-step
                v-for="(step, i) in GRIN_CARD_SHIPPING_STATUS_STEPS"
                v-show="showStep(step)"
                :key="i"
                :title="step.title"
            />
        </g-steps>

        <g-button
            v-if="pendingActivation"
            shape="round"
            theme="live"
            type="primary"
            size="large"
            class="g-mt-5"
            :loading="isLinkLoading"
            :disabled="windowIsOpen || isLinkLoading"
            @click="handleAction()"
        >
            {{ buttonText }}
        </g-button>
    </div>
</template>

<script>
    import { ref, computed } from 'vue-demi';
    import moment from 'moment-timezone';
    import {
        GRIN_CARD_STATUS_ACTIVE,
        GRIN_CARD_STATUS_INACTIVE,
        GRIN_CARD_SHIPPING_STATUS_PENDING,
        GRIN_CARD_SHIPPING_STATUS_DELIVERED,
        GRIN_CARD_SHIPPING_STATUS_RETURNED,
        GRIN_CARD_SHIPPING_STATUS_FAILURE,
        GRIN_CARD_SHIPPING_STATUS_LABEL_ESTIMATED_DELIVERY,
        GRIN_CARD_SHIPPING_STATUS_LABEL_DELIVERED,
        GRIN_CARD_SHIPPING_STATUS_LABEL_PENDING,
        GRIN_CARD_SHIPPING_STATUS_LABEL_CANCELED,
        GRIN_CARD_SHIPPING_STATUS_STEPS,
        GRIN_CARD_BUTTON_ACTIVATE_MY_CARD,
        GRIN_CARD_BUTTON_TRACK_MY_CARD,
        GRIN_CARD_BUTTON_REORDER_MY_CARD
    } from '../../../../../app-v2/constants/payouts';
    import {
        EVENT_NAME_PAYOUTS_GRIN_PAY_CARD_TRACKED,
        EVENT_NAME_PAYOUTS_GRIN_PAY_CARD_ACTIVATION_STARTED
    } from '../../../../../app-v2/constants/analytics-events';
    import { popup } from '../../../../helpers/utils';
    import useOnboarding from '../../../../composables/use-onboarding';
    import useToggle from '../../../../composables/use-toggle';
    import useMessage from '../../../../composables/use-message';
    import useEventTracking from '../../../../composables/use-event-tracking';
    import useWhiteLabeling from '../../../../composables/use-white-labeling';
    import useContactStore from '../../../../../stores/contact';

    export default {
        name: 'PayoutCardActivation',
        props: {
            payoutAccount: {
                type: Object,
                default: null
            }
        },
        setup(props, context) {
            const contact = useContactStore().data;
            const { message } = useMessage();
            const events = useEventTracking();
            const cardStatus = props.payoutAccount?.payout_card?.status;
            const shippingStatus = props.payoutAccount?.payout_card?.shipping_status;
            const currentStepId = ref(0);
            const description = ref('');

            const index = cardStatus === GRIN_CARD_STATUS_ACTIVE ? GRIN_CARD_STATUS_ACTIVE : shippingStatus;
            currentStepId.value = GRIN_CARD_SHIPPING_STATUS_STEPS[index]?.id;
            description.value = GRIN_CARD_SHIPPING_STATUS_STEPS[index]?.description;

            const pendingActivation = computed(() => {
                return cardStatus === GRIN_CARD_STATUS_INACTIVE;
            });

            const delivered = computed(() => {
                return shippingStatus === GRIN_CARD_SHIPPING_STATUS_DELIVERED;
            });

            const deliveryFailed = computed(() => {
                return [GRIN_CARD_SHIPPING_STATUS_RETURNED, GRIN_CARD_SHIPPING_STATUS_FAILURE].includes(shippingStatus);
            });

            const showStep = (step) => {
                return step.show || step.id === currentStepId.value;
            };

            const deliveryDate = computed(() => {
                if (shippingStatus === GRIN_CARD_SHIPPING_STATUS_PENDING) {
                    return GRIN_CARD_SHIPPING_STATUS_LABEL_PENDING;
                } else if (deliveryFailed.value) {
                    return GRIN_CARD_SHIPPING_STATUS_LABEL_CANCELED;
                }

                return moment(props.payoutAccount?.payout_card?.shipping_eta).format('dddd, MMMM DD, YYYY');
            });

            const deliveryStatus = computed(() => {
                let status = delivered.value
                    ? GRIN_CARD_SHIPPING_STATUS_LABEL_DELIVERED
                    : GRIN_CARD_SHIPPING_STATUS_LABEL_ESTIMATED_DELIVERY;

                status += `: ${deliveryDate.value}`;
                return status;
            });

            const buttonText = computed(() => {
                if (deliveryFailed.value) {
                    return GRIN_CARD_BUTTON_REORDER_MY_CARD;
                } else if (delivered.value) {
                    return GRIN_CARD_BUTTON_ACTIVATE_MY_CARD;
                }

                return GRIN_CARD_BUTTON_TRACK_MY_CARD;
            });

            let checkActivation = false;
            const handleAction = () => {
                if (delivered.value) {
                    checkActivation = true;
                    openDashboard();
                } else if (deliveryFailed.value) {
                    openDashboard();
                } else {
                    openTracking();
                }
            };

            const { createDashboardLink } = useOnboarding();
            const { currentValue: isLinkLoading, setTrue: linkLoading, setFalse: linkDoneLoading } = useToggle(false);

            const openDashboard = async () => {
                linkLoading();
                try {
                    const res = await createDashboardLink(contact.id);
                    const link = res?.data?.url;
                    if (!link) {
                        message.error('Oops! Could not retrieve a dashboard link for activating your card.');
                        linkDoneLoading();
                        events.trackEvent(EVENT_NAME_PAYOUTS_GRIN_PAY_CARD_ACTIVATION_STARTED, getGrinPayEventPayload(), false);
                        return;
                    }
                    events.trackEvent(EVENT_NAME_PAYOUTS_GRIN_PAY_CARD_ACTIVATION_STARTED, getGrinPayEventPayload());
                    openWindow(link);
                } catch (e) {
                    message.error(`Oops! Something went wrong: ${message.responseError(e)}`);
                    events.trackEvent(EVENT_NAME_PAYOUTS_GRIN_PAY_CARD_ACTIVATION_STARTED, getGrinPayEventPayload(), false);
                }
                linkDoneLoading();
            };

            const windowIsOpen = ref(false);
            const openWindow = (link) => {
                const activationWindow = popup(link, 480, 640);
                windowIsOpen.value = true;
                setWindowCloseWatcher(activationWindow);
            };

            const setWindowCloseWatcher = (activationWindow) => {
                const interval = setInterval(() => {
                    if (activationWindow.closed) {
                        windowIsOpen.value = false;
                        context.emit('refresh-payout-account', GRIN_CARD_STATUS_ACTIVE);
                        clearInterval(interval);
                    }
                }, 500);
            };

            const openTracking = () => {
                const trackingUrl = props.payoutAccount?.payout_card?.shipping_tracking_url;
                if (trackingUrl) {
                    window.location.href = trackingUrl;
                    events.trackEvent(EVENT_NAME_PAYOUTS_GRIN_PAY_CARD_TRACKED, getGrinPayEventPayload());
                } else {
                    message.error('Oops! Could not retrieve the tracking url for your card.');
                    events.trackEvent(EVENT_NAME_PAYOUTS_GRIN_PAY_CARD_TRACKED, getGrinPayEventPayload(), false);
                }
            };

            const getGrinPayEventPayload = () => {
                return {
                    contact_id: contact.id,
                    is_whitelabled: useWhiteLabeling().isWhiteLabeled(),
                    has_pending_payout: contact.has_pending_payout,
                    has_additional_payout_method: props.payoutAccount?.external_account?.payout_methods.length > 0
                };
            };

            return {
                GRIN_CARD_SHIPPING_STATUS_STEPS,
                currentStepId,
                showStep,
                pendingActivation,
                deliveryStatus,
                deliveryFailed,
                delivered,
                buttonText,
                description,

                handleAction,
                windowIsOpen,
                isLinkLoading
            };
        }
    };
</script>

<style lang="scss" scoped>
    .payout-card-activation {
        border: 1px solid $g-color-grey-4;
        border-radius: $g-border-radius-md;

        ::v-deep .ant-steps-item-tail {
            padding: 18px 0 0px !important;
        }

        ::v-deep .ant-steps-item-content {
            min-height: auto;
            padding-bottom: 8px;

            .ant-steps-item-title {
                font-size: $g-font-size-p;
            }
        }

        ::v-deep .ant-steps-item:last-child .ant-steps-item-content {
            padding-bottom: 0;
        }
    }
</style>
